/**
 * Market & Schedule (§7.1, view 6): the price chart, a 48-hour schedule editor with a price
 * overlay and feasibility shading from the what-if run, the revenue ledger, water value, and
 * reserve / AGC status.
 */

import { useEffect, useState } from 'react';
import { useSession } from '../../store/session';
import { useDispatch, useEngine } from '../useEngine';
import { getSite } from '../../sites';
import { Trend } from '../charts/Trend';
import { Readout } from '../widgets/Readout';
import { Gloss } from '../glossary/GlossaryDrawer';
import { money, moneyExact, energy } from '../format';

export function MarketView(): JSX.Element {
  const snap = useSession((s) => s.snapshot);
  const site = getSite(useSession((s) => s.siteId));
  const whatIf = useSession((s) => s.whatIf);
  const whatIfPending = useSession((s) => s.whatIfPending);
  const dispatch = useDispatch();
  const engine = useEngine();
  const [plan, setPlan] = useState<number[]>(() => new Array<number>(48).fill(0));
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (!snap || seeded) return;
    setPlan(snap.control.schedule.some((v) => v > 0) ? snap.control.schedule.slice() : new Array<number>(48).fill(Math.round(snap.exportMW)));
    setSeeded(true);
  }, [snap, seeded]);

  if (!snap) return <div style={{ padding: 24 }}>—</div>;
  const maxMW = site.powerhouse.ratedMW * site.powerhouse.count;

  return (
    <div style={{ padding: 'var(--s3)', display: 'grid', gap: 'var(--s3)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 'var(--s2)' }}>
        <Readout label="Price" value={`$${snap.price.toFixed(0)}`} unit="/MWh" tone={snap.price < 0 ? 'warn' : 'normal'} />
        <Readout
          label="Water value"
          value={`$${snap.waterValue.toFixed(0)}`}
          unit="/MWh"
          size="sm"
          tone={snap.price > snap.waterValue ? 'ok' : 'dim'}
          hint="Sell when the price beats it."
        />
        <Readout label="Generation" value={Math.round(snap.exportMW).toString()} unit="MW" size="sm" />
        <Readout
          label="Reserve"
          value={Math.round(snap.reserveMW).toString()}
          unit={`/ ${snap.reserveObligationMW} MW`}
          size="sm"
          tone={snap.reserveMW >= snap.reserveObligationMW ? 'ok' : 'warn'}
        />
        <Readout label="Energy" value={energy(snap.energyMWh).value} unit={energy(snap.energyMWh).unit} size="sm" />
        <Readout label="Net" value={money(snap.net)} size="sm" tone={snap.net < 0 ? 'alarm' : 'normal'} />
      </div>

      <section className="panel">
        <div className="label" style={{ marginBottom: 6 }}>
          Price and generation
        </div>
        <Trend
          height={170}
          yLabel="$/MWh"
          fmt={(v) => `$${v.toFixed(0)}`}
          series={[
            { x: snap.history.minuteT, y: snap.history.price, colour: 'var(--ok)', width: 1.6, label: 'price' },
          ]}
          thresholds={[
            { value: snap.waterValue, colour: 'var(--water-shallow)', label: 'water value' },
            { value: 0, colour: 'var(--warn)', label: '$0' },
          ]}
        />
      </section>

      <section className="panel">
        <div className="label" style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
          <span>
            48-hour schedule · <Gloss id="agc">AGC</Gloss>
          </span>
          <span style={{ textTransform: 'none', color: 'var(--ink-dim)' }}>
            drag a bar to set the hour · plant maximum {maxMW.toLocaleString('en-US')} MW
          </span>
        </div>
        <ScheduleEditor plan={plan} setPlan={setPlan} maxMW={maxMW} snap={snap} />
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          <button
            className="primary"
            onClick={() => {
              dispatch({ t: 'schedule', hours: plan });
              useSession.getState().setToast('Schedule submitted');
            }}
          >
            Submit schedule
          </button>
          <button
            disabled={whatIfPending}
            onClick={() =>
              engine.whatIf(
                [50 as const, 90 as const].map((p) => ({
                  schedule: plan,
                  horizonH: 48,
                  percentile: p,
                  autoSpill: snap.control.autoSpillArmed,
                  autoSpillMaxOpening: snap.control.autoSpillMaxOpening,
                })),
              )
            }
          >
            {whatIfPending ? 'Testing…' : 'Test against forecast'}
          </button>
          <button onClick={() => setPlan(new Array<number>(48).fill(Math.round(snap.exportMW)))}>Flat at current</button>
          <button
            onClick={() =>
              setPlan((p) =>
                p.map((_, h) => {
                  const hourOfDay = (snap.hour + h) % 24;
                  const peak = (hourOfDay >= 7 && hourOfDay <= 10) || (hourOfDay >= 17 && hourOfDay <= 20);
                  return peak ? Math.round(maxMW * 0.85) : Math.round(maxMW * 0.35);
                }),
              )
            }
          >
            Shape to peaks
          </button>
          <button
            className={snap.agcEnabled ? 'armed' : undefined}
            onClick={() => dispatch({ t: 'agc', enabled: !snap.agcEnabled })}
            title={`AGC pays $${site.grid.agcPayment}/MW·h for ±${site.grid.agcRangeMW} MW of range.`}
          >
            AGC {snap.agcEnabled ? 'on' : 'off'}
          </button>
        </div>

        {whatIf && whatIf.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div className="label" style={{ marginBottom: 4 }}>
              Feasibility — this plan against the forecast
            </div>
            <div style={{ display: 'grid', gap: 4 }}>
              {whatIf.map((t) => (
                <div
                  key={t.percentile}
                  className="num"
                  style={{
                    display: 'flex',
                    gap: 16,
                    padding: '4px 8px',
                    background: 'var(--surface-2)',
                    borderLeft: `3px solid ${t.breachedMfl ? 'var(--alarm)' : t.breachedFsl ? 'var(--warn)' : 'var(--ok)'}`,
                  }}
                >
                  <span style={{ width: 44 }}>p{t.percentile}</span>
                  <span>peak pool {t.peakHeadwater.toFixed(2)} m</span>
                  <span>peak gauge {Math.round(t.peakGauge).toLocaleString('en-US')} m³/s</span>
                  <span style={{ color: t.finalNet < 0 ? 'var(--alarm)' : 'var(--ok)' }}>{money(t.finalNet)}</span>
                  <span style={{ color: t.breachedMfl ? 'var(--alarm)' : t.breachedFsl ? 'var(--warn)' : 'var(--ok)' }}>
                    {t.breachedMfl ? 'breaches MFL' : t.breachedFsl ? 'enters surcharge' : 'feasible'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="panel">
        <div className="label" style={{ marginBottom: 8 }}>
          Revenue ledger
        </div>
        <table style={{ width: '100%', fontSize: 13 }} className="num">
          <tbody>
            <Row label="Energy" value={snap.revenue - 0} />
            <Row label="Penalties" value={-snap.penalties} />
            <Row label="Start costs" value={-snap.startCosts} />
            <tr style={{ borderTop: '1px solid var(--line)' }}>
              <td style={{ paddingTop: 6, fontWeight: 600 }}>Net</td>
              <td style={{ paddingTop: 6, textAlign: 'right', fontWeight: 600, color: snap.net < 0 ? 'var(--alarm)' : 'var(--ok)' }}>
                {moneyExact(snap.net)}
              </td>
            </tr>
          </tbody>
        </table>

        {Object.keys(snap.penaltyByKind).length > 0 && (
          <>
            <div className="label" style={{ margin: '12px 0 4px' }}>
              Penalties by cause
            </div>
            <table style={{ width: '100%', fontSize: 12 }} className="num">
              <tbody>
                {Object.entries(snap.penaltyByKind)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, v]) => (
                    <tr key={k}>
                      <td style={{ color: 'var(--ink-dim)' }}>{k.replace(/-/g, ' ')}</td>
                      <td style={{ textAlign: 'right', color: 'var(--alarm)' }}>{moneyExact(-v)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </>
        )}
      </section>
    </div>
  );
}

function Row({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <tr>
      <td style={{ color: 'var(--ink-dim)' }}>{label}</td>
      <td style={{ textAlign: 'right', color: value < 0 ? 'var(--alarm)' : 'var(--ink)' }}>{moneyExact(value)}</td>
    </tr>
  );
}

function ScheduleEditor({
  plan,
  setPlan,
  maxMW,
  snap,
}: {
  plan: number[];
  setPlan(p: number[] | ((p: number[]) => number[])): void;
  maxMW: number;
  snap: NonNullable<ReturnType<typeof useSession.getState>['snapshot']>;
}): JSX.Element {
  const H = 150;
  const W = 960;
  const bw = W / 48;

  const setHour = (h: number, clientY: number, rect: DOMRect): void => {
    const frac = 1 - (clientY - rect.top) / rect.height;
    const mw = Math.round(Math.max(0, Math.min(1, frac)) * maxMW);
    setPlan((p) => p.map((v, i) => (i === h ? mw : v)));
  };

  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        role="application"
        aria-label="48 hour generation schedule editor"
        onPointerDown={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const h = Math.floor(((e.clientX - rect.left) / rect.width) * 48);
          if (h >= 0 && h < 48) setHour(h, e.clientY, rect);
        }}
        onPointerMove={(e) => {
          if (e.buttons !== 1) return;
          const rect = e.currentTarget.getBoundingClientRect();
          const h = Math.floor(((e.clientX - rect.left) / rect.width) * 48);
          if (h >= 0 && h < 48) setHour(h, e.clientY, rect);
        }}
        style={{ background: 'var(--surface-0)', border: '1px solid var(--line)', borderRadius: 3, touchAction: 'none' }}
      >
        {plan.map((mw, h) => {
          const hh = (mw / maxMW) * (H - 18);
          const hourOfDay = (snap.hour + h) % 24;
          const peak = (hourOfDay >= 7 && hourOfDay <= 10) || (hourOfDay >= 17 && hourOfDay <= 20);
          return (
            <g key={h}>
              <rect x={h * bw} y={H - 18 - hh} width={bw - 1} height={hh} fill={peak ? 'var(--water-shallow)' : 'var(--water)'} />
              {hourOfDay === 0 && <line x1={h * bw} x2={h * bw} y1={0} y2={H - 18} stroke="var(--line)" />}
              {h % 6 === 0 && (
                <text x={h * bw + 2} y={H - 5} fontSize={9} fill="var(--ink-dim)" className="num">
                  {String(hourOfDay).padStart(2, '0')}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="num" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--ink-dim)' }}>
        <span>now</span>
        <span>
          plan total {Math.round(plan.reduce((a, b) => a + b, 0)).toLocaleString('en-US')} MWh over 48 h
        </span>
        <span>+48 h</span>
      </div>
    </div>
  );
}
